// This optional code is used to register a service worker.
// register() is not called by default.

// This lets the app load faster on subsequent visits in production, and gives
// it offline capabilities. However, it also means that developers (and users)
// will only see deployed updates on subsequent visits to a page, after all the
// existing tabs open on the page have been closed, since previously cached
// resources are updated in the background.

// To learn more about the benefits of this model and instructions on how to
// opt-in, read https://bit.ly/CRA-PWA

const isLocalhost = Boolean(
  window.location.hostname === 'localhost' ||
    // [::1] is the IPv6 localhost address.
    window.location.hostname === '[::1]' ||
    // 127.0.0.0/8 are considered localhost for IPv4.
    window.location.hostname.match(
      /^127(?:\.(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)){3}$/
    )
);

export function register(config) {
  if (process.env.NODE_ENV === 'production' && 'serviceWorker' in navigator) {
    // The URL constructor is available in all browsers that support SW.
    const publicUrl = new URL(process.env.PUBLIC_URL, window.location.href);
    if (publicUrl.origin !== window.location.origin) {
      // Our service worker won't work if PUBLIC_URL is on a different origin
      // from what our page is served on. This might happen if a CDN is used to
      // serve assets; see https://github.com/facebook/create-react-app/issues/2374
      return;
    }

    window.addEventListener('load', () => {
      const swUrl = `${process.env.PUBLIC_URL}/service-worker.js`;

      // Unified message handler for all service worker messages
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.addEventListener('message', event => {
          if (event.data && event.data.type) {
            switch (event.data.type) {
              case 'SW_UPDATE_AVAILABLE':
                // Service worker has updated content available
                if (config && config.onUpdate) {
                  config.onUpdate(event.data.registration);
                }
                break;
              case 'SW_UPDATE_READY':
                // The service worker has been updated and is ready to take control
                window.location.reload();
                break;
              default:
                // Handle any other message types if needed in the future
                break;
            }
          }
        });

        // Listen for controlling service worker changes
        navigator.serviceWorker.addEventListener('controllerchange', () => {
          // A new service worker has taken control, reload to get fresh content
          window.location.reload();
        });

        // Handle service worker errors - clear cache if loading fails
        navigator.serviceWorker.addEventListener('error', event => {
          console.error('Service worker error:', event);
          // Clear caches if there's a loading error
          if (typeof caches !== 'undefined') {
            caches.keys().then(cacheNames => {
              return Promise.all(
                cacheNames.map(cacheName => {
                  console.log('Deleting cache:', cacheName);
                  return caches.delete(cacheName);
                })
              );
            }).catch(err => {
              console.error('Error clearing caches:', err);
            });
          }
        });
      }

      if (isLocalhost) {
        // This is running on localhost. Let's check if a service worker still exists or not.
        checkValidServiceWorker(swUrl, config);

        // Add some additional logging to localhost, pointing developers to the
        // service worker/PWA documentation.
        navigator.serviceWorker.ready.then(() => {
          console.log(
            'This web app is being served cache-first by a service ' +
              'worker. To learn more, visit https://bit.ly/CRA-PWA'
          );
        });
      } else {
        // Is not localhost. Just register service worker
        registerValidSW(swUrl, config);
      }
    });
  }
}

function registerValidSW(swUrl, config) {
  navigator.serviceWorker
    .register(swUrl)
    .then(registration => {
      registration.onupdatefound = () => {
        const installingWorker = registration.installing;
        if (installingWorker == null) {
          return;
        }
        installingWorker.onstatechange = () => {
          if (installingWorker.state === 'installed') {
            if (navigator.serviceWorker.controller) {
              // At this point, the updated precached content has been fetched,
              // but the previous service worker will still serve the older
              // content until all client tabs are closed.
              console.log(
                'New content is available and will be used when all ' +
                  'tabs for this page are closed. See https://bit.ly/CRA-PWA.'
              );

              // Execute callback
              if (config && config.onUpdate) {
                config.onUpdate(registration);
              }
            } else {
              // At this point, everything has been precached.
              // It's the perfect time to display a
              // "Content is cached for offline use." message.
              console.log('Content is cached for offline use.');

              // Execute callback
              if (config && config.onSuccess) {
                config.onSuccess(registration);
              }
            }
          }
        };
      };
    })
    .catch(error => {
      console.error('Error during service worker registration:', error);
      
      // If registration fails, try to clear all caches and unregister any existing service workers
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.getRegistrations().then(registrations => {
          registrations.forEach(registration => {
            console.log('Unregistering existing service worker');
            registration.unregister();
          });
        });
      }
      
      if (typeof caches !== 'undefined') {
        caches.keys().then(cacheNames => {
          return Promise.all(
            cacheNames.map(cacheName => {
              console.log('Clearing cache due to SW registration error:', cacheName);
              return caches.delete(cacheName);
            })
          );
        }).then(() => {
          console.log('All caches cleared due to service worker registration error');
        }).catch(cacheError => {
          console.error('Error clearing caches:', cacheError);
        });
      }
    });
}

function checkValidServiceWorker(swUrl, config) {
  // Check if the service worker can be found. If it can't reload the page.
  fetch(swUrl, {
    headers: { 'Service-Worker': 'script' },
  })
    .then(response => {
      // Ensure service worker exists, and that we really are getting a JS file.
      const contentType = response.headers.get('content-type');
      if (
        response.status === 404 ||
        (contentType != null && contentType.indexOf('javascript') === -1)
      ) {
        // No service worker found. Probably a different app. Reload the page.
        navigator.serviceWorker.ready.then(registration => {
          registration.unregister().then(() => {
            window.location.reload();
          });
        });
      } else {
        // Service worker found. Proceed as normal.
        registerValidSW(swUrl, config);
      }
    })
    .catch(() => {
      console.log(
        'No internet connection found. App is running in offline mode.'
      );
    });
}

export function unregister() {
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.ready
      .then(registration => {
        registration.unregister();
      })
      .catch(error => {
        console.error(error.message);
      });
  }
}

// Utility function to clear all caches and unregister service workers
// This can be called when there are caching issues like mismatched file names
export function clearAllCaches() {
  return new Promise((resolve, reject) => {
    if ('serviceWorker' in navigator) {
      // Unregister all service workers
      navigator.serviceWorker.getRegistrations().then(registrations => {
        const unregisterPromises = registrations.map(registration => {
          console.log('Unregistering service worker');
          return registration.unregister();
        });
        
        return Promise.all(unregisterPromises);
      }).then(() => {
        // Clear all caches
        if (typeof caches !== 'undefined') {
          return caches.keys().then(cacheNames => {
            const deletePromises = cacheNames.map(cacheName => {
              console.log('Deleting cache:', cacheName);
              return caches.delete(cacheName);
            });
            return Promise.all(deletePromises);
          });
        }
        return Promise.resolve();
      }).then(() => {
        console.log('All service workers unregistered and caches cleared');
        resolve();
      }).catch(error => {
        console.error('Error clearing caches and service workers:', error);
        reject(error);
      });
    } else {
      // If no service worker support, just try to clear caches
      if (typeof caches !== 'undefined') {
        caches.keys().then(cacheNames => {
          const deletePromises = cacheNames.map(cacheName => {
            console.log('Deleting cache:', cacheName);
            return caches.delete(cacheName);
          });
          return Promise.all(deletePromises);
        }).then(() => {
          console.log('All caches cleared');
          resolve();
        }).catch(error => {
          console.error('Error clearing caches:', error);
          reject(error);
        });
      } else {
        resolve();
      }
    }
  });
}
